import {
  BenefitsFinancialImpactCalculationDto,
  CurrentPortfolioSSSummaryDto,
  FinancialParametersCalculationDto,
  PBData,
} from '@flexyoo/api';
import {
  ICartSimulationBalanceView,
  ICartSimulationBenefitView,
  ICartSimulationBudgetType,
  ICartSimulationView,
  EmployeeSelectedBenefit,
} from '@flexyoo/employee/models';

type PrimesAndBonusesBudgetType = Exclude<ICartSimulationBudgetType, 'salary-sacrifice'>;

/**
 * The Primes & Bonuses budget types in display order, each with its field in the
 * per-benefit `PBData` and in the before/after parameters. Bonus+ is the bonus
 * with annual holiday fee (`funding.title.bonus-plus`), P13 the end-of-year premium.
 *
 * The "budget" side of the backend amounts is used throughout, like the funding
 * amounts of the "Selected benefits" section of the report.
 */
const PRIMES_AND_BONUSES_BUDGET_TYPES: {
  budgetType: PrimesAndBonusesBudgetType;
  benefitField: Exclude<keyof PBData, 'Total'>;
  parameterField: keyof FinancialParametersCalculationDto &
    ('PBWithVacationPay' | 'PBWithoutVacationPay' | 'EndOfYearPremium' | 'PBSetByHr');
}[] = [
  {
    budgetType: 'bonus-plus',
    benefitField: 'PBWithVacationPay',
    parameterField: 'PBWithVacationPay',
  },
  {
    budgetType: 'bonus-minus',
    benefitField: 'PBWithoutVacationPay',
    parameterField: 'PBWithoutVacationPay',
  },
  { budgetType: 'p13', benefitField: 'P13', parameterField: 'EndOfYearPremium' },
  { budgetType: 'hr-budget', benefitField: 'PBSetByHr', parameterField: 'PBSetByHr' },
];

/**
 * The impact calculation only knows the monthly salary sacrifice of a benefit, and
 * the yearly figure depends on the employee's P13 settings, so the yearly amount is
 * taken from the cart (`YearlyGrossSSAmount`) instead of being derived here.
 *
 * Only the cart benefits the backend calculated are listed: it leaves out those HR
 * rejected, which the cart still holds.
 */
const buildBenefits = (
  impact: BenefitsFinancialImpactCalculationDto,
  cartBenefits: EmployeeSelectedBenefit[]
): ICartSimulationBenefitView[] => {
  const impactByBenefitId = new Map(
    (impact.BenefitsImpact ?? []).map((benefitImpact) => [benefitImpact.BenefitId, benefitImpact])
  );
  const calculatedBenefitIds = new Set([
    ...impactByBenefitId.keys(),
    ...(impact.BenefitsWithNoImpact ?? []).map((benefitImpact) => benefitImpact.BenefitId),
  ]);

  return cartBenefits
    .filter((benefit) => calculatedBenefitIds.has(benefit.id))
    .map((benefit) => {
      const benefitImpact = impactByBenefitId.get(benefit.id);
      const primesAndBonuses = benefitImpact?.PBGrossAmounts;
      const salarySacrifice = benefit.ssPeriodBudget ?? 0;

      const budgetTypes: ICartSimulationBudgetType[] = PRIMES_AND_BONUSES_BUDGET_TYPES.filter(
        ({ benefitField }) => (primesAndBonuses?.[benefitField] ?? 0) > 0
      ).map(({ budgetType }) => budgetType);

      if (benefitImpact?.HasSalarySacrifice || salarySacrifice > 0) {
        budgetTypes.push('salary-sacrifice');
      }

      return {
        benefitId: benefit.id,
        name: benefit.name,
        budget: (primesAndBonuses?.Total ?? 0) + salarySacrifice,
        budgetTypes,
      };
    });
};

/**
 * A budget type is listed when the employee was assigned it, even if the cart
 * used it up; the Primes & Bonuses balances are the "after" figures of the same
 * calculation the report shows, the Salary Sacrifice one is the convertible
 * salary still available, as on the Salary Sacrifice card of the basket.
 */
const buildBalances = (
  impact: BenefitsFinancialImpactCalculationDto,
  ssBasket: CurrentPortfolioSSSummaryDto | null | undefined
): ICartSimulationBalanceView[] => {
  const before = impact.FinancialParametersBefore;
  const after = impact.FinancialParametersAfter;

  const balances: ICartSimulationBalanceView[] = before?.HasPB
    ? PRIMES_AND_BONUSES_BUDGET_TYPES.filter(
        ({ parameterField }) => (before[parameterField] ?? 0) > 0
      ).map(({ budgetType, parameterField }) => ({
        budgetType,
        amount: after?.[parameterField] ?? 0,
      }))
    : [];

  if (ssBasket?.ConvertibleGrossSalary) {
    balances.push({
      budgetType: 'salary-sacrifice',
      amount: ssBasket.AvailableConvertibleAmount,
    });
  }

  return balances;
};

/** Assembles the "Cart simulation" card from the impact calculation, the cart and the SS basket. */
export const buildCartSimulationView = (
  impact: BenefitsFinancialImpactCalculationDto,
  cartBenefits: EmployeeSelectedBenefit[],
  ssBasket: CurrentPortfolioSSSummaryDto | null | undefined
): ICartSimulationView => ({
  benefits: buildBenefits(impact, cartBenefits),
  balances: buildBalances(impact, ssBasket),
});
